import React, { useEffect, useState } from 'react';
import { CustomerVerificationError, SmsCodeContent, SmsSendingInfo } from 'cpnl-common';
import { Error, WithDefaultCpIntegrationErrors } from '@cp-shared-19/common-utilities';
import { CustomAxiosError, handleSendSms, handleSubmitForm } from './Utils';
import { SmsIdentificationUi } from './ui';
import {
    RegistrationLoadingPlaceholder,
    useAnalyticsActionTracker,
    useAnalyticsFormTracker,
    useAnalyticsPageViewTracker,
} from '@cp-shared-19/frontend-ui';
import { useCmsContent } from '../../../utils';
import { withLoadingAndNoConnectionHandler } from '../../integration-wrapper';
import { dashboardPagePath } from '../../navigation/paths';
import { useHistory } from 'react-router';
import { Spinner } from '../../spinner';

const SmsIdentificationWithHandlers = withLoadingAndNoConnectionHandler(SmsIdentificationUi);
const MAX_SMS_LIMIT = 3;

const getSendSmsErrorCode = (error?: CustomAxiosError): string | undefined => {
    const response = error?.error?.response;
    const data = response?.data;
    return data?.response?.code ?? data?.code ?? (response ? 'CP_API_NOT_REACHABLE' : undefined);
};
type SmsSendingRequestProps = {
    isLoading: boolean;
    error?: CustomAxiosError;
};

type FormSubmissionRequestProps = {
    isLoading: boolean;
    error?: Error<WithDefaultCpIntegrationErrors<CustomerVerificationError>>;
};

export type SmsIdentificationProps = {
    customerNotFoundCallback: () => void;
    goBack: () => void;
};

export const SmsIdentification: React.FC<SmsIdentificationProps> = ({ customerNotFoundCallback, goBack }) => {
    const [smsSendingRequest, setSmsSendingRequest] = useState<SmsSendingRequestProps>({ isLoading: true });
    const [formSubmissionRequest, setFormSubmissionRequest] = useState<FormSubmissionRequestProps>({
        isLoading: false,
    });
    const [isMaxSmsSend, setIsMaxSmsSend] = useState(false);
    const [isResendLinkHidden, setIsResendLinkHidden] = useState(false);
    const [maskedPhoneNumber, setMaskedPhoneNumber] = useState('');
    const [smsPageContent, isConetntLoading, contentLoadingError] = useCmsContent<SmsCodeContent>('sms-code-page');

    useAnalyticsPageViewTracker('enterSmsCode');
    const { onError: trackOnError } = useAnalyticsFormTracker({
        confirmError: 'onConfirmIdentiyAuthFailed',
    });
    const { onAction: trackOnSuccess } = useAnalyticsActionTracker('onEnterSmsCodeSuccess');

    const history = useHistory();

    const sendSmsCode = (): void => {
        const onSucess = (data: SmsSendingInfo): void => {
            const { mobileNumber, numberOfSmsSent } = data;
            setMaskedPhoneNumber(`${mobileNumber.slice(0, 2)}*****${mobileNumber.slice(7)}`);
            if (numberOfSmsSent > MAX_SMS_LIMIT) setIsMaxSmsSend(true);
            if (numberOfSmsSent >= MAX_SMS_LIMIT) setIsResendLinkHidden(true);
            setSmsSendingRequest({ isLoading: false });
        };

        const onError = (error: CustomAxiosError): void => {
            if (error?.error?.response?.data?.response?.code === 'CUSTOMER_NOT_FOUND')
                return customerNotFoundCallback();
            setSmsSendingRequest({ isLoading: false, error });
        };

        handleSendSms(onSucess, onError);
    };

    const handleSubmit = ({ smsCode }: { smsCode: string }): void => {
        setFormSubmissionRequest({ isLoading: true });
        const onSuccess = (): void => {
            trackOnSuccess();
            history.push(dashboardPagePath());
        };
        const onError = (error: Error<WithDefaultCpIntegrationErrors<CustomerVerificationError>>): void => {
            trackOnError();
            setFormSubmissionRequest({ isLoading: false, error });
        };

        handleSubmitForm(smsCode, onSuccess, onError);
    };

    useEffect(() => {
        sendSmsCode();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {formSubmissionRequest.isLoading && <Spinner fullPage={true} />}
            <SmsIdentificationWithHandlers
                isLoading={isConetntLoading || smsSendingRequest.isLoading}
                hasError={!!contentLoadingError}
                smsPageContent={smsPageContent}
                maskedPhoneNumber={maskedPhoneNumber}
                isMaxSmsSend={isMaxSmsSend}
                isResendLinkHidden={isResendLinkHidden}
                sendSmsCode={sendSmsCode}
                handleSubmit={handleSubmit}
                goBack={goBack}
                sendSmsErrorCode={getSendSmsErrorCode(smsSendingRequest?.error)}
                verificationErrorCode={formSubmissionRequest?.error?.code}
                loadingPlaceholder={<RegistrationLoadingPlaceholder />}
            />
        </>
    );
};
